import { cn } from '@/lib/cn'
import type { StatusMeta, StatusTone } from '@/lib/status'

/**
 * The one renderer for a status.
 *
 * Two presentations of the same fact, chosen by density rather than by taste:
 *
 * - `dot`   — for a table. A filled pill on all 25 rows is louder than the
 *             value deserves and competes with the numbers for attention.
 * - `badge` — for a header or a detail panel, where the state appears once and
 *             is worth a beat of emphasis.
 */
export type StatusVariant = 'badge' | 'dot'

const BADGE_TONES: Record<StatusTone, string> = {
  neutral: 'border-line bg-surface-sunken text-ink-secondary',
  accent: 'border-accent-ring/45 bg-accent-soft text-accent-text',
  good: 'border-good/30 bg-good-soft text-good',
  warning: 'border-warning/30 bg-warning-soft text-warning',
  serious: 'border-serious/30 bg-serious-soft text-serious',
  critical: 'border-critical/30 bg-critical-soft text-critical',
}

const DOT_TONES: Record<StatusTone, string> = {
  neutral: 'bg-ink-faint',
  accent: 'bg-accent',
  good: 'bg-good',
  warning: 'bg-warning',
  serious: 'bg-serious',
  critical: 'bg-critical',
}

function Busy({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 12 12"
      className={cn('h-2.5 w-2.5 shrink-0 animate-spin', className)}
      aria-hidden="true"
    >
      <circle
        cx="6"
        cy="6"
        r="4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeDasharray="21"
        strokeDashoffset="14"
      />
    </svg>
  )
}

export interface StatusProps {
  meta: StatusMeta
  variant?: StatusVariant
  /** Overrides the registry's label, for the portal's own wording. */
  label?: string
  className?: string
}

export function Status({ meta, variant = 'badge', label, className }: StatusProps) {
  const text = label?.trim() || meta.label

  if (variant === 'dot') {
    return (
      <span className={cn('flex min-w-0 items-center gap-1.5', className)} title={meta.hint}>
        {meta.busy ? (
          <Busy className={cn('text-accent')} />
        ) : (
          <span
            aria-hidden="true"
            className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT_TONES[meta.tone])}
          />
        )}
        <span className="truncate text-ink-secondary">{text}</span>
      </span>
    )
  }

  return (
    <span
      title={meta.hint}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5',
        'text-2xs font-medium whitespace-nowrap',
        BADGE_TONES[meta.tone],
        className,
      )}
    >
      {meta.busy ? (
        <Busy />
      ) : (
        <span
          aria-hidden="true"
          className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT_TONES[meta.tone])}
        />
      )}
      {text}
    </span>
  )
}
